import { useEffect, useState } from 'react';
import { usePractices } from '../../hooks/useAccounts';
import { useCommunicationsSummary } from '../../hooks/useCommunicationsSummary';
import { useCommunicationsInsights } from '../../hooks/useCommunicationsInsights';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import type { CommunicationsInsightsGranularity } from '../../services/communicationsInsights';
import { CommunicationsOverTimeCard } from '../../components/accounts/overview/CommunicationsOverTimeCard';
import { CommunicationsPerPersonCard } from '../../components/accounts/overview/CommunicationsPerPersonCard';
import { HubCategoryCard } from '../../components/accounts/overview/HubCategoryCard';
import { HubStatsRow } from '../../components/accounts/overview/HubStatsRow';
import { LatestCommunicationsCard } from '../../components/accounts/overview/LatestCommunicationsCard';
import { PracticesNeedingAttentionCard } from '../../components/accounts/overview/PracticesNeedingAttentionCard';
import { TopPracticesByCommunicationsCard } from '../../components/accounts/overview/TopPracticesByCommunicationsCard';

// Accounts › Overview - the section's landing page. What came in, what's
// unresolved, and which practices need someone today. The section-wide
// "Practices" total/addedThisMonth is fetched once here (limit 1 - the
// cheapest usePractices call that still returns meta.summary) and passed
// down to HubStatsRow; every other card fetches its own data, same
// convention as OverviewTab's per-practice cards - except the three
// communications-insights cards (CommunicationsOverTimeCard,
// TopPracticesByCommunicationsCard, CommunicationsPerPersonCard), which
// share one query and one granularity toggle owned by this page (see
// useCommunicationsInsights).
export function AccountsOverview() {
    usePageTitle('Accounts overview');
    const { setTitle, setSubtitle } = usePageHeader();

    const {
        meta,
        isError: isPracticesError,
        refetch: refetchPractices
    } = usePractices({ page: 1, limit: 1 });
    const {
        summary,
        isError: isSummaryError,
        refetch: refetchSummary
    } = useCommunicationsSummary();

    // One shared query drives "Communications over time", "Top practices"
    // and "Communications per person" - the toggle lives here so all three
    // cards (and their window label) move together instead of each owning
    // its own state.
    const [granularity, setGranularity] = useState<CommunicationsInsightsGranularity>('day');
    const {
        insights,
        isLoading: isInsightsLoading,
        isError: isInsightsError,
        refetch: refetchInsights
    } = useCommunicationsInsights(granularity);

    const retryHubStats = () => {
        if (isPracticesError) void refetchPractices();
        if (isSummaryError) void refetchSummary();
    };

    useEffect(() => {
        setTitle('Accounts');
        setSubtitle(`${meta.total} practices`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [meta.total, setTitle, setSubtitle]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto px-8 py-4">
                <HubStatsRow
                    practicesTotal={meta.total}
                    practicesAddedThisMonth={meta.summary.addedThisMonth}
                    summary={summary}
                    practicesError={isPracticesError}
                    summaryError={isSummaryError}
                    onRetry={retryHubStats}
                />

                <CommunicationsOverTimeCard
                    className="shrink-0 h-[280px]"
                    series={insights?.series ?? []}
                    granularity={granularity}
                    onGranularityChange={setGranularity}
                    isLoading={isInsightsLoading}
                    isError={isInsightsError}
                    onRetry={() => void refetchInsights()}
                />

                <div className="flex-1 min-h-0 flex gap-4">
                    <div className="flex-[2] min-h-0 flex flex-col gap-4">
                        <LatestCommunicationsCard className="flex-1 min-h-0" />
                        <PracticesNeedingAttentionCard className="flex-1 min-h-0" />
                    </div>

                    <div className="flex-1 min-h-0 flex flex-col gap-4">
                        <HubCategoryCard className="flex-1 min-h-0" />
                        <TopPracticesByCommunicationsCard
                            className="flex-1 min-h-0"
                            topPractices={insights?.topPractices ?? []}
                            granularity={granularity}
                            isLoading={isInsightsLoading}
                            isError={isInsightsError}
                            onRetry={() => void refetchInsights()}
                        />
                        <CommunicationsPerPersonCard
                            className="flex-1 min-h-0"
                            byUser={insights?.byUser ?? []}
                            granularity={granularity}
                            isLoading={isInsightsLoading}
                            isError={isInsightsError}
                            onRetry={() => void refetchInsights()}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
